import { EventBlock, isBranch } from '@basehub/data/replicache/index'
import * as React from 'react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuTrigger,
} from '~/sotano-kit/primitives/context-menu'
import { useRepoStore } from '../../repo-store'
import { useEditorMode } from '../../editor-mode-provider'
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { Button } from '~/sotano-kit/primitives/button'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { EventRow } from '~/app/api/v2/events/schema'
import { toast } from 'sonner'
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogDescription,
} from '~/sotano-kit/primitives/dialog'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { Slot } from '@radix-ui/react-slot'

type TableContextMenuProps = {
  children: React.ReactNode
  selectedRows: EventRow[]
  parentBlock: EventBlock
  disabled?: boolean
}

export const TableContextMenu = React.forwardRef<
  HTMLDivElement,
  TableContextMenuProps
>((props, ref) => {
  if (props.disabled) return props.children

  return <TableContextMenuInner {...props} ref={ref} />
})

export const TableContextMenuInner = React.forwardRef<
  HTMLDivElement,
  TableContextMenuProps
>(({ children, selectedRows, disabled, parentBlock }, ref) => {
  const editorMode = useEditorMode()
  const { branchOrCommit } = useBranchOrCommitSegment()

  const readOnly =
    editorMode !== 'write:all' &&
    editorMode !== 'write:value-only' &&
    editorMode !== 'write:value-and-title'

  disabled = disabled || selectedRows.length === 0 || readOnly
  const teamId = useRepoStore((s) => s.teamId)
  const repoId = useRepoStore((s) => s.repoId)

  const handleDelete = React.useCallback(
    async (rows: EventRow[]) => {
      if (rows.length === 0) return
      if (!branchOrCommit) return
      const noun = rows.length > 1 ? 'rows' : 'row'
      try {
        const ids = JSON.stringify(rows.map((r) => r.id).filter(Boolean))
        toast.loading(`Deleting ${noun}...`, { id: `delete-row-${ids}` })
        const res = await fetch(
          `/api/ad-hoc/get-event-key?blockId=${idToSourceBlockId(
            parentBlock
          )}&teamId=${teamId}&repoId=${repoId}&scope=query&branchId=${isBranch(branchOrCommit) ? branchOrCommit.id : branchOrCommit.branchId}`
        )
        const key = await res.text()
        const res2 = await fetch(`/api/v2/events?ids=${ids}&key=${key}`, {
          method: 'DELETE',
        })

        if (res2.ok) {
          toast.success(`${noun} deleted`, {
            id: `delete-row-${ids}`,
            classNames: { title: 'capitalize-first' },
          })
        } else {
          toast.error(`Couldn't delete ${noun}`, {
            id: `delete-row-${ids}`,
          })
        }
      } catch (e) {
        console.error(e)
        toast.error(`Failed to delete ${noun}`)
      }
    },
    [branchOrCommit, parentBlock, repoId, teamId]
  )

  const [rowsToDelete, setRowsToDelete] = React.useState<false | EventRow[]>(
    false
  )

  const actions = React.useMemo(
    () =>
      [
        {
          key: 'delete',
          type: 'action',
          label: 'Delete',
          disabled,
          icon: <TrashIcon width={16} height={16} />,
          onSelect: async (e: React.MouseEvent) => {
            if (e.shiftKey) {
              handleDelete(selectedRows)
              return
            }

            setRowsToDelete(selectedRows)
          },
        },
      ] as const,
    [disabled, handleDelete, selectedRows]
  )

  return (
    <>
      {Boolean(selectedRows.length && !readOnly) && (
        <IconButton
          onClick={actions[0].onSelect}
          className="absolute right-[calc(100%+12px)] max-w-[52px] w-full py-0.5 rounded-md leading-normal top-1.5 pr-1.5 pl-2 bg-dark-shade hover:bg-dark-shade-hover border border-dark-border-solid text-xs text-dark-muted tabular-nums"
        >
          {/* amount of rows, but if selected is more than 1k, use a shorthand */}
          {Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 1,
          }).format(selectedRows.length)}
          <TrashIcon width={14} height={14} className="shrink-0" />
        </IconButton>
      )}
      <ContextMenu>
        <ContextMenuTrigger disabled={disabled}>
          <Slot
            onKeyDown={(e) => {
              if (e.key === 'Backspace') {
                e.stopPropagation()
                e.preventDefault()

                if (e.shiftKey) {
                  handleDelete(selectedRows)
                  return
                }
                setRowsToDelete(selectedRows)
              }
            }}
          >
            {children}
          </Slot>
        </ContextMenuTrigger>
        <ContextMenuContent
          ref={ref}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <ContextMenuLabel className="text-ellipsis whitespace-nowrap overflow-hidden">
            <span className="text-dark-muted">Editing </span>
            {selectedRows.length === 1
              ? '1 row'
              : `${selectedRows.length} rows`}
          </ContextMenuLabel>

          {actions.map((action) => {
            switch (action.type) {
              case 'action':
                return (
                  <ContextMenuItem
                    className="space-x-2 text-xs leading-4"
                    key={action.key}
                    disabled={action.disabled}
                    onClick={action.onSelect}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </ContextMenuItem>
                )
              default:
                console.error({ action })
                throw new Error('Unknown action type')
            }
          })}
        </ContextMenuContent>
      </ContextMenu>
      <Dialog
        open={!!rowsToDelete}
        onOpenChange={(change) => {
          if (!change) {
            setRowsToDelete(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader className="grid grid-cols-[1fr_auto]">
            <DialogTitle>
              Are you sure you want to delete{' '}
              {rowsToDelete && rowsToDelete.length} row
              {rowsToDelete && rowsToDelete.length > 1 ? 's' : ''}?
            </DialogTitle>
            <DialogClose asChild>
              <IconButton>
                <Cross2Icon />
              </IconButton>
            </DialogClose>
            <DialogDescription className="col-span-full">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter divider>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="tertiary"
              onClick={() => {
                if (rowsToDelete) {
                  handleDelete(rowsToDelete)
                  setRowsToDelete(false)
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})
